import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import { IconButton, Collapse } from "@mui/material";
import {
  KeyboardArrowDownIcon as KeyboardArrowDown,
  KeyboardArrowRightIcon as KeyboardArrowRight,
} from "../../../../icons";
import { BotStep, SortOrder } from "../../../../api/graphql";
import { useRootStore } from "../../../../store/contexts";
import { getApiAsync } from "../../../../api";
import { BotPhase } from "../../../../api/graphql";

type BotPhasePanelProps = { parentId: string };

export const BotPhasePanel: React.FC<BotPhasePanelProps> = ({ parentId }) => {
  const rootStore = useRootStore();
  // Extraídos del rootStore para que ESLint vea la cadena de deps del useEffect.
  const { langStore, authStore, activityStore } = rootStore;
  const [items, setItems] = useState<BotPhase[]>([]);
  const [loading, setLoading] = useState(true);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [subItemsMap, setSubItemsMap] = useState<Map<string, BotStep[]>>(
    new Map()
  );
  const [loadingSub, setLoadingSub] = useState<Set<string>>(new Set());

  const toggleExpand = async (itemId: string) => {
    const newExpanded = new Set(expanded);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
      // Cargar sub-items si no están cargados
      if (!subItemsMap.has(itemId) && !loadingSub.has(itemId)) {
        setLoadingSub((prev) => new Set(prev).add(itemId));
        try {
          const api = await getApiAsync(
            rootStore.authStore,
            rootStore.activityStore
          );
          const result = await api.botStepListWithOffset({
            where: { parent_BotPhase_steps: { id: { equals: itemId } } },
            orderBy: [{ stepNumber: SortOrder.Asc }],
            take: 100,
            offset: 0,
          });
          setSubItemsMap((prev) =>
            new Map(prev).set(itemId, result.botStepListWithOffset as BotStep[])
          );
        } catch (error) {
          console.error("Failed to load botStep:", error);
        } finally {
          setLoadingSub((prev) => {
            const next = new Set(prev);
            next.delete(itemId);
            return next;
          });
        }
      }
    }
    setExpanded(newExpanded);
  };
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        if (cancelled) return;
        const api = await getApiAsync(authStore, activityStore);
        const result = await api.botPhaseListWithOffset({
          where: { parent_BotRun_phases: { id: { equals: parentId } } },
          orderBy: [{ phaseNumber: SortOrder.Asc }],
          take: 100,
          offset: 0,
        });
        if (!cancelled) {
          setItems(result.botPhaseListWithOffset as BotPhase[]);
        }
      } catch (error) {
        console.error("Failed to load botPhase:", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [parentId, authStore, activityStore]);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
      >
        {langStore.BotPhaseLabels?.p || "BotPhase"}
        {loading && <CircularProgress size={16} />}
        {!loading && items.length === 0 && (
          <Typography component="span" variant="body2" color="text.secondary">
            ({langStore.noRecordsMessage})
          </Typography>
        )}
      </Typography>
      {!loading && items.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40}></TableCell>
              <TableCell>
                {langStore.BotPhasePhaseNumberLabel || "phaseNumber"}
              </TableCell>
              <TableCell>
                {langStore.BotPhasePhaseNameLabel || "phaseName"}
              </TableCell>
              <TableCell>{langStore.BotPhaseStatusLabel || "status"}</TableCell>
              <TableCell>
                {langStore.BotPhaseDurationLabel || "duration"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <React.Fragment key={item.id}>
                <TableRow
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    rootStore.openDrawerView("botPhase", item.id);
                  }}
                >
                  <TableCell width={40}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpand(item.id);
                      }}
                    >
                      {expanded.has(item.id) ? (
                        <KeyboardArrowDown fontSize="small" />
                      ) : (
                        <KeyboardArrowRight fontSize="small" />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>{String(item.phaseNumber ?? "-")}</TableCell>
                  <TableCell>{String(item.phaseName ?? "-")}</TableCell>
                  <TableCell>{String(item.status ?? "-")}</TableCell>
                  <TableCell>{String(item.duration ?? "-")}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                    <Collapse in={expanded.has(item.id)} unmountOnExit>
                      <Box
                        sx={{
                          pl: 6,
                          pr: 2,
                          py: 1,
                          backgroundColor: "background.paper",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {langStore.BotStepLabels?.p || "BotStep"}
                          {loadingSub.has(item.id) && (
                            <CircularProgress size={12} />
                          )}
                        </Typography>
                        {subItemsMap.has(item.id) &&
                          subItemsMap.get(item.id)!.length > 0 && (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>
                                    {langStore.BotStepStepNumberLabel ||
                                      "stepNumber"}
                                  </TableCell>
                                  <TableCell>
                                    {langStore.BotStepStepIdLabel || "stepId"}
                                  </TableCell>
                                  <TableCell>
                                    {langStore.BotStepStatusLabel || "status"}
                                  </TableCell>
                                  <TableCell>
                                    {langStore.BotStepDurationLabel ||
                                      "duration"}
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {subItemsMap.get(item.id)!.map((subItem) => (
                                  <TableRow
                                    key={subItem.id}
                                    hover
                                    sx={{ cursor: "pointer" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      rootStore.openDrawerView(
                                        "botStep",
                                        subItem.id
                                      );
                                    }}
                                  >
                                    <TableCell>
                                      {String(subItem.stepNumber ?? "-")}
                                    </TableCell>
                                    <TableCell>
                                      {String(subItem.stepId ?? "-")}
                                    </TableCell>
                                    <TableCell>
                                      {String(subItem.status ?? "-")}
                                    </TableCell>
                                    <TableCell>
                                      {String(subItem.duration ?? "-")}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        {subItemsMap.has(item.id) &&
                          subItemsMap.get(item.id)!.length === 0 && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ py: 1 }}
                            >
                              {langStore.noRecordsMessage}
                            </Typography>
                          )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};
